/** Canonical form-builder tree: pages[].items and group.children. No flat draft. */

export const UNASSIGNED_ID = 'unassigned'
export const TRAY_ID = 'tray'
export const PAGES_CONTAINER = 'pages'

export type FieldGroupChild =
  | { type: 'field'; fieldName: string }
  | { type: 'group'; groupId: string }

export type FieldGroup = {
  id: string
  name: string
  children?: FieldGroupChild[]
  fieldNames: string[]
}

export type PageItem = { type: 'field'; key: string } | { type: 'group'; key: string }

export type PageDef = {
  id: string
  name: string
  items: PageItem[]
}

export type TreeState = {
  pages: PageDef[]
  fieldGroups: FieldGroup[]
}

export type ParentId = `page:${string}` | `group:${string}` | typeof TRAY_ID | typeof PAGES_CONTAINER

export function fieldId(name: string): string {
  return `field:${name}`
}

export function groupId(id: string): string {
  return `group:${id}`
}

export function pageId(id: string): string {
  return `page:${id}`
}

export function containerIdFor(parent: ParentId): string {
  return `container:${parent}`
}

export function parseTreeId(id: string): { kind: 'page' | 'group' | 'field' | 'tray' | 'pages' | 'container'; value: string } | null {
  if (id === TRAY_ID || id === `container:${TRAY_ID}`) return { kind: 'tray', value: TRAY_ID }
  if (id === PAGES_CONTAINER || id === `container:${PAGES_CONTAINER}`) return { kind: 'pages', value: PAGES_CONTAINER }
  if (id.startsWith('container:')) {
    const inner = parseTreeId(id.slice('container:'.length))
    if (!inner) return { kind: 'container', value: id }
    return inner
  }
  if (id.startsWith('page:')) return { kind: 'page', value: id.slice(5) }
  if (id.startsWith('group:')) return { kind: 'group', value: id.slice(6) }
  if (id.startsWith('field:')) return { kind: 'field', value: id.slice(6) }
  return null
}

export function groupChildren(g: FieldGroup | undefined | null): FieldGroupChild[] {
  if (!g) return []
  if (Array.isArray(g.children)) return g.children
  return (g.fieldNames || []).map(fieldName => ({ type: 'field' as const, fieldName }))
}

export function syncFieldNames(g: FieldGroup): FieldGroup {
  const children = groupChildren(g)
  return {
    ...g,
    children,
    fieldNames: children.filter((c): c is { type: 'field'; fieldName: string } => c.type === 'field').map(c => c.fieldName),
  }
}

export function syncAllGroups(groups: FieldGroup[]): FieldGroup[] {
  return groups.map(syncFieldNames)
}

function groupById(groups: FieldGroup[]): Map<string, FieldGroup> {
  return new Map(groups.map(g => [g.id, syncFieldNames(g)]))
}

export function collectLeafFieldNames(groups: FieldGroup[], rootId: string, visited: Set<string> = new Set()): string[] {
  if (visited.has(rootId)) return []
  visited.add(rootId)
  const map = groupById(groups)
  const g = map.get(rootId)
  if (!g) return []
  const out: string[] = []
  for (const ch of groupChildren(g)) {
    if (ch.type === 'field') out.push(ch.fieldName)
    else out.push(...collectLeafFieldNames(groups, ch.groupId, visited))
  }
  return out
}

export function collectDescendantGroupIds(groups: FieldGroup[], rootId: string, visited: Set<string> = new Set()): Set<string> {
  const ids = new Set<string>()
  if (visited.has(rootId)) return ids
  visited.add(rootId)
  const map = groupById(groups)
  const g = map.get(rootId)
  if (!g) return ids
  for (const ch of groupChildren(g)) {
    if (ch.type === 'group') {
      ids.add(ch.groupId)
      for (const nested of collectDescendantGroupIds(groups, ch.groupId, visited)) ids.add(nested)
    }
  }
  return ids
}

export function collectGroupIdsOnPages(pages: PageDef[], groups: FieldGroup[]): Set<string> {
  const ids = new Set<string>()
  for (const p of pages) {
    for (const it of p.items) {
      if (it.type !== 'group') continue
      ids.add(it.key)
      for (const nested of collectDescendantGroupIds(groups, it.key)) ids.add(nested)
    }
  }
  return ids
}

export function collectPlacedFieldNames(pages: PageDef[], groups: FieldGroup[]): Set<string> {
  const names = new Set<string>()
  const nestedGroupIds = new Set<string>()
  for (const g of groups) {
    for (const ch of groupChildren(g)) {
      if (ch.type === 'group') nestedGroupIds.add(ch.groupId)
      if (ch.type === 'field') names.add(ch.fieldName)
    }
  }
  for (const p of pages) {
    for (const it of p.items) {
      if (it.type === 'field') names.add(it.key)
      else {
        for (const fn of collectLeafFieldNames(groups, it.key)) names.add(fn)
      }
    }
  }
  return names
}

export function getTrayItems(state: TreeState, parsedFieldNames: string[]): PageItem[] {
  const groups = syncAllGroups(state.fieldGroups)
  const pages = state.pages.filter(p => p.id !== UNASSIGNED_ID)
  const groupsOnPages = collectGroupIdsOnPages(pages, groups)
  const placedFields = collectPlacedFieldNames(pages, groups)
  const nestedIds = new Set<string>()
  for (const g of groups) {
    for (const ch of groupChildren(g)) {
      if (ch.type === 'group') nestedIds.add(ch.groupId)
    }
  }
  const items: PageItem[] = []
  for (const g of groups) {
    if (!groupsOnPages.has(g.id) && !nestedIds.has(g.id)) {
      items.push({ type: 'group', key: g.id })
    }
  }
  for (const name of parsedFieldNames) {
    if (!placedFields.has(name)) items.push({ type: 'field', key: name })
  }
  return items
}

export type FlatRow = {
  id: string
  kind: 'page' | 'group' | 'field' | 'tray'
  parentId: string | null
  depth: number
  label: string
  hasChildren: boolean
  descendantCount: number
  fieldName?: string
}

function countGroupDescendants(groups: FieldGroup[], gid: string): number {
  const g = groups.find(x => x.id === gid)
  if (!g) return 0
  let n = 0
  for (const ch of groupChildren(g)) {
    n += 1
    if (ch.type === 'group') n += countGroupDescendants(groups, ch.groupId)
  }
  return n
}

/** Visible rows for a VS Code-style tree: collapsed (and dragging) parents hide their children. */
export function flattenVisible(
  state: TreeState,
  parsedFieldNames: string[],
  collapsedIds: Set<string> = new Set(),
  hideChildrenOf: string | null = null,
): FlatRow[] {
  const clean = sanitizeTree(state, parsedFieldNames)
  const groups = clean.fieldGroups
  const out: FlatRow[] = []
  const hide = (id: string) => id === hideChildrenOf || collapsedIds.has(id)

  const pushGroup = (gid: string, parentId: string, depth: number, visited: Set<string>) => {
    if (visited.has(gid)) return
    visited.add(gid)
    const g = groups.find(x => x.id === gid)
    if (!g) return
    const children = groupChildren(g)
    const id = groupId(gid)
    out.push({
      id,
      kind: 'group',
      parentId,
      depth,
      label: g.name,
      hasChildren: children.length > 0,
      descendantCount: countGroupDescendants(groups, gid),
    })
    if (hide(id)) return
    for (const ch of children) {
      if (ch.type === 'field') {
        out.push({
          id: fieldId(ch.fieldName),
          kind: 'field',
          parentId: id,
          depth: depth + 1,
          label: ch.fieldName,
          hasChildren: false,
          descendantCount: 0,
          fieldName: ch.fieldName,
        })
      } else {
        pushGroup(ch.groupId, id, depth + 1, visited)
      }
    }
  }

  for (const p of clean.pages) {
    const pid = pageId(p.id)
    const descendantCount = p.items.reduce((n, it) => {
      if (it.type === 'field') return n + 1
      return n + 1 + countGroupDescendants(groups, it.key)
    }, 0)
    out.push({
      id: pid,
      kind: 'page',
      parentId: null,
      depth: 0,
      label: p.name,
      hasChildren: p.items.length > 0,
      descendantCount,
    })
    if (!hide(pid)) {
      for (const it of p.items) {
        if (it.type === 'field') {
          out.push({
            id: fieldId(it.key),
            kind: 'field',
            parentId: pid,
            depth: 1,
            label: it.key,
            hasChildren: false,
            descendantCount: 0,
            fieldName: it.key,
          })
        } else {
          pushGroup(it.key, pid, 1, new Set())
        }
      }
    }
  }

  const tray = getTrayItems(clean, parsedFieldNames)
  out.push({
    id: TRAY_ID,
    kind: 'tray',
    parentId: null,
    depth: 0,
    label: 'Not on a page',
    hasChildren: tray.length > 0,
    descendantCount: tray.length,
  })
  if (!hide(TRAY_ID)) {
    for (const it of tray) {
      if (it.type === 'field') {
        out.push({
          id: fieldId(it.key),
          kind: 'field',
          parentId: TRAY_ID,
          depth: 1,
          label: it.key,
          hasChildren: false,
          descendantCount: 0,
          fieldName: it.key,
        })
      } else {
        pushGroup(it.key, TRAY_ID, 1, new Set())
      }
    }
  }

  return out
}

function arrayMove<T>(items: T[], from: number, to: number): T[] {
  const next = items.slice()
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item)
  return next
}

function canHaveChildren(row: FlatRow | undefined): boolean {
  if (!row) return false
  return row.kind === 'page' || row.kind === 'group' || row.kind === 'tray'
}

function dragDepthFromOffset(offset: number, indentationWidth: number): number {
  return Math.round(offset / indentationWidth)
}

/**
 * Official dnd-kit tree projection: vertical slot from `over`, depth from horizontal offset.
 * Pages stay at depth 0. Fields cannot contain children.
 */
export function getTreeProjection(
  items: FlatRow[],
  activeId: string,
  overId: string,
  dragOffset: number,
  indentationWidth: number,
): { depth: number; parentId: string | null } | null {
  const overIndex = items.findIndex(i => i.id === overId)
  const activeIndex = items.findIndex(i => i.id === activeId)
  if (overIndex < 0 || activeIndex < 0) return null

  const active = items[activeIndex]
  const over = items[overIndex]
  if (active.kind === 'page') return { depth: 0, parentId: null }
  if (active.kind === 'tray') return null

  // Dropping on a folder row nests into it (Finder / VS Code). Indent vs siblings
  // is handled when the pointer is over a field, using horizontal offset below.
  if (canHaveChildren(over) && over.id !== activeId) {
    return { depth: over.depth + 1, parentId: over.id }
  }

  const moved = arrayMove(items, activeIndex, overIndex)
  const previous = moved[overIndex - 1]
  const nextItem = moved[overIndex + 1]
  const projectedDepth = active.depth + dragDepthFromOffset(dragOffset, indentationWidth)

  const maxDepth = canHaveChildren(previous) ? previous.depth + 1 : (previous ? previous.depth : 1)
  const minDepth = nextItem ? nextItem.depth : 1
  let depth = Math.min(maxDepth, Math.max(minDepth, projectedDepth))
  if (depth < 1) depth = 1

  let parentId: string | null
  if (!previous) {
    parentId = TRAY_ID
  } else if (depth === previous.depth) {
    parentId = previous.parentId
  } else if (depth > previous.depth) {
    parentId = previous.id
  } else {
    parentId = moved.slice(0, overIndex).reverse().find(item => item.depth === depth)?.parentId ?? previous.parentId
  }

  if (parentId === activeId) return { depth: active.depth, parentId: active.parentId }
  return { depth, parentId }
}

export function projectionToMove(
  items: FlatRow[],
  activeId: string,
  overId: string,
  projected: { depth: number; parentId: string | null },
): { parent: ParentId; index: number } | null {
  const activeIndex = items.findIndex(i => i.id === activeId)
  const overIndex = items.findIndex(i => i.id === overId)
  if (activeIndex < 0 || overIndex < 0) return null
  const active = items[activeIndex]
  if (active.kind === 'page') {
    const pages = items.filter(i => i.kind === 'page')
    let target: FlatRow | undefined = items[overIndex]
    while (target && target.kind !== 'page') {
      target = items.find(i => i.id === target!.parentId)
    }
    const targetId = target?.id ?? activeId
    const index = pages.findIndex(p => p.id === targetId)
    return { parent: PAGES_CONTAINER, index: Math.max(0, index) }
  }

  const parentId = projected.parentId ?? TRAY_ID
  const over = items[overIndex]
  if (over.id === parentId) {
    return toMove(parentId, Number.MAX_SAFE_INTEGER)
  }

  const moved = arrayMove(items, activeIndex, overIndex)
  const movedAt = moved.findIndex(i => i.id === activeId)
  const siblingsBefore = moved.slice(0, movedAt).filter(i => i.parentId === parentId).length
  const visibleSiblings = moved.filter(i => i.id !== activeId && i.parentId === parentId)
  const index = visibleSiblings.length === 0 ? Number.MAX_SAFE_INTEGER : siblingsBefore
  return toMove(parentId, index)
}

function toMove(parentId: string, index: number): { parent: ParentId; index: number } | null {
  if (parentId === TRAY_ID) return { parent: TRAY_ID, index }
  const parsed = parseTreeId(parentId)
  if (!parsed) return null
  if (parsed.kind === 'page') return { parent: `page:${parsed.value}`, index }
  if (parsed.kind === 'group') return { parent: `group:${parsed.value}`, index }
  return null
}

function childToPageItem(ch: FieldGroupChild): PageItem {
  return ch.type === 'field' ? { type: 'field', key: ch.fieldName } : { type: 'group', key: ch.groupId }
}

function pageItemToChild(it: PageItem): FieldGroupChild {
  return it.type === 'field' ? { type: 'field', fieldName: it.key } : { type: 'group', groupId: it.key }
}

export function findItemLocation(
  state: TreeState,
  itemId: string,
  parsedFieldNames: string[] = [],
): { parent: ParentId; index: number } | null {
  const parsed = parseTreeId(itemId)
  if (!parsed) return null
  const pages = state.pages.filter(p => p.id !== UNASSIGNED_ID)
  const groups = syncAllGroups(state.fieldGroups)

  if (parsed.kind === 'page') {
    const index = pages.findIndex(p => p.id === parsed.value)
    if (index < 0) return null
    return { parent: PAGES_CONTAINER, index }
  }

  if (parsed.kind === 'group') {
    for (const p of pages) {
      const index = p.items.findIndex(it => it.type === 'group' && it.key === parsed.value)
      if (index >= 0) return { parent: `page:${p.id}`, index }
    }
    for (const g of groups) {
      const index = groupChildren(g).findIndex(ch => ch.type === 'group' && ch.groupId === parsed.value)
      if (index >= 0) return { parent: `group:${g.id}`, index }
    }
    const tray = getTrayItems({ pages, fieldGroups: groups }, parsedFieldNames)
    const index = tray.findIndex(it => it.type === 'group' && it.key === parsed.value)
    if (index >= 0) return { parent: TRAY_ID, index }
    return null
  }

  if (parsed.kind === 'field') {
    for (const p of pages) {
      const index = p.items.findIndex(it => it.type === 'field' && it.key === parsed.value)
      if (index >= 0) return { parent: `page:${p.id}`, index }
    }
    for (const g of groups) {
      const index = groupChildren(g).findIndex(ch => ch.type === 'field' && ch.fieldName === parsed.value)
      if (index >= 0) return { parent: `group:${g.id}`, index }
    }
    const tray = getTrayItems({ pages, fieldGroups: groups }, parsedFieldNames)
    const index = tray.findIndex(it => it.type === 'field' && it.key === parsed.value)
    if (index >= 0) return { parent: TRAY_ID, index }
    return null
  }

  return null
}

function getParentItems(state: TreeState, parent: ParentId, parsedFieldNames: string[]): PageItem[] {
  if (parent === TRAY_ID) return getTrayItems(state, parsedFieldNames)
  if (parent === PAGES_CONTAINER) return state.pages.filter(p => p.id !== UNASSIGNED_ID).map(p => ({ type: 'group', key: p.id }))
  const parsed = parseTreeId(parent)
  if (!parsed) return []
  if (parsed.kind === 'page') {
    const page = state.pages.find(p => p.id === parsed.value)
    return page ? [...page.items] : []
  }
  if (parsed.kind === 'group') {
    const g = state.fieldGroups.find(gr => gr.id === parsed.value)
    return groupChildren(g).map(childToPageItem)
  }
  return []
}

function setParentItems(state: TreeState, parent: ParentId, items: PageItem[]): TreeState {
  if (parent === TRAY_ID || parent === PAGES_CONTAINER) return state
  const parsed = parseTreeId(parent)
  if (!parsed) return state
  if (parsed.kind === 'page') {
    return {
      ...state,
      pages: state.pages.map(p => (p.id === parsed.value ? { ...p, items } : p)),
    }
  }
  if (parsed.kind === 'group') {
    return {
      ...state,
      fieldGroups: state.fieldGroups.map(g => {
        if (g.id !== parsed.value) return g
        return syncFieldNames({ ...g, children: items.map(pageItemToChild) })
      }),
    }
  }
  return state
}

function removeItemFromTree(state: TreeState, itemId: string, parsedFieldNames: string[]): TreeState {
  const loc = findItemLocation(state, itemId, parsedFieldNames)
  if (!loc) return state
  if (loc.parent === PAGES_CONTAINER) {
    const parsed = parseTreeId(itemId)
    if (parsed?.kind !== 'page') return state
    return { ...state, pages: state.pages.filter(p => p.id !== parsed.value) }
  }
  if (loc.parent === TRAY_ID) return state
  const items = getParentItems(state, loc.parent, parsedFieldNames).filter((_, i) => i !== loc.index)
  return setParentItems(state, loc.parent, items)
}

function wouldCreateCycle(state: TreeState, movingGroupId: string, toParent: ParentId): boolean {
  if (toParent === TRAY_ID || toParent === PAGES_CONTAINER) return false
  const parsed = parseTreeId(toParent)
  if (!parsed) return false
  if (parsed.kind === 'page') return false
  if (parsed.kind === 'group') {
    if (parsed.value === movingGroupId) return true
    return collectDescendantGroupIds(state.fieldGroups, movingGroupId).has(parsed.value)
  }
  return false
}

export function sanitizeTree(state: TreeState, parsedFieldNames: string[]): TreeState {
  const seenFields = new Set<string>()
  const seenGroups = new Set<string>()

  const dedupeItems = (items: PageItem[], allowNewGroups: boolean): PageItem[] => {
    const out: PageItem[] = []
    for (const it of items) {
      if (it.type === 'field') {
        if (seenFields.has(it.key)) continue
        seenFields.add(it.key)
        out.push(it)
      } else {
        if (seenGroups.has(it.key)) continue
        if (!allowNewGroups && !state.fieldGroups.some(g => g.id === it.key)) continue
        seenGroups.add(it.key)
        out.push(it)
      }
    }
    return out
  }

  const groups = syncAllGroups(state.fieldGroups).map(g => {
    const children = dedupeItems(groupChildren(g).map(childToPageItem), true).map(pageItemToChild)
    return syncFieldNames({ ...g, children })
  })

  const pages = state.pages
    .filter(p => p.id !== UNASSIGNED_ID)
    .map(p => ({ ...p, items: dedupeItems(p.items, true) }))

  // Nested groups must not also sit on a page.
  const nested = new Set<string>()
  for (const g of groups) {
    for (const ch of groupChildren(g)) {
      if (ch.type === 'group') nested.add(ch.groupId)
    }
  }
  const pagesWithoutNested = pages.map(p => ({
    ...p,
    items: p.items.filter(it => !(it.type === 'group' && nested.has(it.key))),
  }))

  return { pages: pagesWithoutNested, fieldGroups: groups }
}

export function ensureDefaultPage(pages: PageDef[]): PageDef[] {
  const real = pages.filter(p => p.id !== UNASSIGNED_ID)
  if (real.length > 0) return real
  return [{ id: 'page_1', name: 'Page 1', items: [] }]
}

export function moveItem(
  state: TreeState,
  itemId: string,
  toParent: ParentId,
  toIndex: number,
  parsedFieldNames: string[] = [],
): TreeState {
  const parsed = parseTreeId(itemId)
  if (!parsed) return state

  if (parsed.kind === 'page') {
    const pages = state.pages.filter(p => p.id !== UNASSIGNED_ID)
    const from = pages.findIndex(p => p.id === parsed.value)
    if (from < 0) return state
    if (toParent !== PAGES_CONTAINER) return state
    const next = [...pages]
    const [page] = next.splice(from, 1)
    const insert = Math.max(0, Math.min(toIndex, next.length))
    next.splice(insert, 0, page)
    return sanitizeTree({ ...state, pages: next }, parsedFieldNames)
  }

  if (parsed.kind === 'group' && wouldCreateCycle(state, parsed.value, toParent)) return state

  const loc = findItemLocation(state, itemId, parsedFieldNames)
  const pageItem: PageItem =
    parsed.kind === 'field' ? { type: 'field', key: parsed.value } : { type: 'group', key: parsed.value }

  if (loc && loc.parent === toParent && toParent !== TRAY_ID && toParent !== PAGES_CONTAINER) {
    const items = getParentItems(state, toParent, parsedFieldNames)
    const next = [...items]
    const [removed] = next.splice(loc.index, 1)
    const insert = Math.max(0, Math.min(toIndex, next.length))
    next.splice(insert, 0, removed)
    return sanitizeTree(setParentItems(state, toParent, next), parsedFieldNames)
  }

  let working = removeItemFromTree(state, itemId, parsedFieldNames)
  if (toParent === TRAY_ID) return sanitizeTree(working, parsedFieldNames)
  if (toParent === PAGES_CONTAINER) return state

  const destItems = getParentItems(working, toParent, parsedFieldNames)
  const insertAt = Math.max(0, Math.min(toIndex, destItems.length))
  const nextItems = [...destItems]
  nextItems.splice(insertAt, 0, pageItem)
  working = setParentItems(working, toParent, nextItems)
  return sanitizeTree(working, parsedFieldNames)
}

/**
 * Drop target → parent + index.
 * page row → child of that page (index 0)
 * group row → nest into that group (index 0)
 * field row → sibling in the field's parent, at the field's index
 * container → end of that container
 */
export function resolveDrop(
  state: TreeState,
  activeId: string,
  overId: string,
  parsedFieldNames: string[] = [],
): { parent: ParentId; index: number } | null {
  const active = parseTreeId(activeId)
  const over = parseTreeId(overId)
  if (!active || !over) return null
  if (activeId === overId) return null

  if (active.kind === 'page') {
    if (over.kind === 'page') {
      const pages = state.pages.filter(p => p.id !== UNASSIGNED_ID)
      const index = pages.findIndex(p => p.id === over.value)
      if (index < 0) return null
      return { parent: PAGES_CONTAINER, index }
    }
    if (over.kind === 'pages') {
      return { parent: PAGES_CONTAINER, index: state.pages.filter(p => p.id !== UNASSIGNED_ID).length }
    }
    return null
  }

  if (over.kind === 'pages') return null

  if (over.kind === 'tray') {
    return { parent: TRAY_ID, index: getTrayItems(state, parsedFieldNames).length }
  }

  if (over.kind === 'page') {
    const page = state.pages.find(p => p.id === over.value)
    return { parent: `page:${over.value}`, index: page ? page.items.length : 0 }
  }

  if (over.kind === 'group') {
    const g = state.fieldGroups.find(gr => gr.id === over.value)
    return { parent: `group:${over.value}`, index: groupChildren(g).length }
  }

  if (over.kind === 'field') {
    const loc = findItemLocation(state, overId, parsedFieldNames)
    if (!loc) return null
    return { parent: loc.parent, index: loc.index }
  }

  return null
}

export function moveSibling(
  state: TreeState,
  itemId: string,
  direction: -1 | 1,
  parsedFieldNames: string[] = [],
): TreeState {
  const loc = findItemLocation(state, itemId, parsedFieldNames)
  if (!loc) return state
  const items = getParentItems(state, loc.parent, parsedFieldNames)
  const nextIndex = loc.index + direction
  if (nextIndex < 0 || nextIndex >= items.length) return state
  return moveItem(state, itemId, loc.parent, nextIndex, parsedFieldNames)
}

export function indentItem(state: TreeState, itemId: string, parsedFieldNames: string[] = []): TreeState {
  const loc = findItemLocation(state, itemId, parsedFieldNames)
  if (!loc || loc.index <= 0) return state
  if (loc.parent === PAGES_CONTAINER) return state
  const items = getParentItems(state, loc.parent, parsedFieldNames)
  const prev = items[loc.index - 1]
  if (!prev || prev.type !== 'group') return state
  const g = state.fieldGroups.find(gr => gr.id === prev.key)
  return moveItem(state, itemId, `group:${prev.key}`, groupChildren(g).length, parsedFieldNames)
}

export function outdentItem(state: TreeState, itemId: string, parsedFieldNames: string[] = []): TreeState {
  const loc = findItemLocation(state, itemId, parsedFieldNames)
  if (!loc) return state
  const parent = parseTreeId(loc.parent)
  if (!parent || parent.kind !== 'group') return state
  const parentLoc = findItemLocation(state, groupId(parent.value), parsedFieldNames)
  if (!parentLoc) return state
  return moveItem(state, itemId, parentLoc.parent, parentLoc.index + 1, parsedFieldNames)
}

/** Fold leftover tray items onto a real page so save/load never uses id "unassigned". */
export function persistPages(state: TreeState, parsedFieldNames: string[]): PageDef[] {
  const clean = sanitizeTree(state, parsedFieldNames)
  const tray = getTrayItems(clean, parsedFieldNames)
  let pages = ensureDefaultPage(clean.pages)
  if (tray.length === 0) return pages
  const last = pages[pages.length - 1]
  const existing = new Set(last.items.map(it => `${it.type}:${it.key}`))
  const extra = tray.filter(it => !existing.has(`${it.type}:${it.key}`))
  pages = pages.map((p, i) => (i === pages.length - 1 ? { ...p, items: [...p.items, ...extra] } : p))
  return pages
}

export function walkSelectableKeys(state: TreeState, parsedFieldNames: string[]): string[] {
  const clean = sanitizeTree(state, parsedFieldNames)
  const out: string[] = []
  const walkGroup = (gid: string, visited: Set<string>) => {
    if (visited.has(gid)) return
    visited.add(gid)
    out.push(groupId(gid))
    const g = clean.fieldGroups.find(gr => gr.id === gid)
    for (const ch of groupChildren(g)) {
      if (ch.type === 'field') out.push(fieldId(ch.fieldName))
      else walkGroup(ch.groupId, visited)
    }
  }
  for (const p of clean.pages) {
    out.push(pageId(p.id))
    for (const it of p.items) {
      if (it.type === 'field') out.push(fieldId(it.key))
      else walkGroup(it.key, new Set())
    }
  }
  for (const it of getTrayItems(clean, parsedFieldNames)) {
    if (it.type === 'field') out.push(fieldId(it.key))
    else walkGroup(it.key, new Set())
  }
  return out
}

export function fieldNamesUnderPage(page: PageDef, groups: FieldGroup[]): string[] {
  const names: string[] = []
  for (const it of page.items) {
    if (it.type === 'field') names.push(it.key)
    else names.push(...collectLeafFieldNames(groups, it.key))
  }
  return names
}

export function insertGroupFromFields(
  state: TreeState,
  group: FieldGroup,
  fieldNames: string[],
  parsedFieldNames: string[] = [],
): TreeState {
  const fieldSet = new Set(fieldNames)
  let working = sanitizeTree(state, parsedFieldNames)
  let insertParent: ParentId = TRAY_ID
  let insertIndex = 0
  const firstId = fieldNames[0] ? fieldId(fieldNames[0]) : null
  if (firstId) {
    const loc = findItemLocation(working, firstId, parsedFieldNames)
    if (loc) {
      insertParent = loc.parent === TRAY_ID ? TRAY_ID : loc.parent
      insertIndex = loc.index
    }
  }
  if (insertParent === PAGES_CONTAINER) {
    insertParent = working.pages[0] ? `page:${working.pages[0].id}` : TRAY_ID
    insertIndex = 0
  }

  working = {
    ...working,
    fieldGroups: working.fieldGroups.map(g => {
      const children = groupChildren(g).filter(ch => ch.type === 'group' || !fieldSet.has(ch.fieldName))
      return syncFieldNames({ ...g, children })
    }).filter(g => groupChildren(g).length > 0 || g.id === group.id),
    pages: working.pages.map(p => ({
      ...p,
      items: p.items.filter(it => !(it.type === 'field' && fieldSet.has(it.key))),
    })),
  }

  const synced = syncFieldNames({
    ...group,
    children: fieldNames.map(fieldName => ({ type: 'field' as const, fieldName })),
  })
  working = { ...working, fieldGroups: [...working.fieldGroups.filter(g => g.id !== synced.id), synced] }

  if (insertParent !== TRAY_ID) {
    if (insertParent.startsWith('page:') && working.pages.length === 0) {
      working = { ...working, pages: ensureDefaultPage([]) }
      insertParent = `page:${working.pages[0].id}`
      insertIndex = 0
    }
    working = moveItem(working, groupId(synced.id), insertParent, insertIndex, parsedFieldNames)
  } else if (working.pages.length > 0) {
    working = moveItem(working, groupId(synced.id), `page:${working.pages[0].id}`, working.pages[0].items.length, parsedFieldNames)
  } else {
    working = { ...working, pages: [{ id: `page_${Date.now()}`, name: 'Page 1', items: [{ type: 'group', key: synced.id }] }] }
  }
  return sanitizeTree(working, parsedFieldNames)
}

export type AutoRadioGroupInput = {
  id: string
  name: string
  fieldNames: string[]
}

export function mergeAutoRadioGroups(
  state: TreeState,
  autoGroups: AutoRadioGroupInput[],
  mode: 'seed' | 'merge',
  parsedFieldNames: string[] = [],
): TreeState {
  if (!autoGroups.length) return sanitizeTree(state, parsedFieldNames)
  const autoMemberNames = new Set(autoGroups.flatMap(g => g.fieldNames))
  const autoIds = new Set(autoGroups.map(g => g.id))
  let groups = syncAllGroups(state.fieldGroups)
  const prevById = new Map(groups.filter(g => g.id.startsWith('radio:') || autoIds.has(g.id)).map(g => [g.id, g]))

  if (mode === 'seed') {
    groups = groups
      .filter(g => !g.id.startsWith('radio:') && !autoIds.has(g.id))
      .map(g => {
        const children = groupChildren(g).filter(ch => ch.type !== 'field' || !autoMemberNames.has(ch.fieldName))
        return syncFieldNames({ ...g, children })
      })
      .filter(g => groupChildren(g).length > 0)
    const seeded = autoGroups.map(g =>
      syncFieldNames({
        id: g.id,
        name: prevById.get(g.id)?.name || g.name,
        children: g.fieldNames.map(fieldName => ({ type: 'field' as const, fieldName })),
        fieldNames: g.fieldNames,
      }),
    )
    groups = [...groups, ...seeded]
    let pages = ensureDefaultPage(state.pages.filter(p => p.id !== UNASSIGNED_ID)).map(p => ({
      ...p,
      items: p.items.filter(it => !(it.type === 'field' && autoMemberNames.has(it.key))),
    }))
    const onPages = collectGroupIdsOnPages(pages, groups)
    const toAdd = autoGroups.filter(g => !onPages.has(g.id)).map(g => ({ type: 'group' as const, key: g.id }))
    if (toAdd.length) {
      pages = pages.map((p, i) => (i === 0 ? { ...p, items: [...p.items, ...toAdd] } : p))
    }
    const clean = sanitizeTree({ pages, fieldGroups: groups }, parsedFieldNames)
    return { pages: persistPages(clean, parsedFieldNames), fieldGroups: clean.fieldGroups }
  }

  // merge: keep existing child order; only add/remove members
  const placedElsewhere = new Set<string>()
  for (const g of groups) {
    if (g.id.startsWith('radio:') || autoIds.has(g.id)) continue
    for (const ch of groupChildren(g)) {
      if (ch.type === 'field') placedElsewhere.add(ch.fieldName)
    }
  }

  const mergedIds = new Set<string>()
  const merged: FieldGroup[] = []
  for (const auto of autoGroups) {
    const existing = prevById.get(auto.id)
    mergedIds.add(auto.id)
    if (existing) {
      const still = new Set(auto.fieldNames)
      const kept = groupChildren(existing).filter(ch => ch.type === 'group' || still.has(ch.fieldName))
      const keptFields = new Set(kept.filter(ch => ch.type === 'field').map(ch => ch.type === 'field' ? ch.fieldName : ''))
      for (const fn of auto.fieldNames) {
        if (!keptFields.has(fn) && !placedElsewhere.has(fn)) {
          kept.push({ type: 'field', fieldName: fn })
        }
      }
      merged.push(syncFieldNames({ ...existing, name: existing.name || auto.name, children: kept }))
    } else {
      const members = auto.fieldNames.filter(fn => !placedElsewhere.has(fn))
      if (!members.length) continue
      merged.push(syncFieldNames({
        id: auto.id,
        name: auto.name,
        children: members.map(fieldName => ({ type: 'field' as const, fieldName })),
        fieldNames: members,
      }))
    }
  }

  const keptUser = groups
    .filter(g => !mergedIds.has(g.id) && !g.id.startsWith('radio:'))
    .map(g => {
      const children = groupChildren(g).filter(ch => ch.type !== 'field' || !autoMemberNames.has(ch.fieldName) || placedElsewhere.has(ch.fieldName) && !merged.some(m => groupChildren(m).some(c => c.type === 'field' && c.fieldName === ch.fieldName)))
      return syncFieldNames({ ...g, children })
    })

  // Members claimed by merged radio groups should not remain in user groups.
  const claimed = new Set<string>()
  for (const g of merged) {
    for (const fn of collectLeafFieldNames(merged, g.id)) claimed.add(fn)
  }
  const userStripped = keptUser.map(g => {
    const children = groupChildren(g).filter(ch => ch.type !== 'field' || !claimed.has(ch.fieldName))
    return syncFieldNames({ ...g, children })
  }).filter(g => groupChildren(g).length > 0)

  const pages = state.pages.filter(p => p.id !== UNASSIGNED_ID).map(p => ({
    ...p,
    items: p.items.filter(it => !(it.type === 'field' && claimed.has(it.key))),
  }))

  return sanitizeTree({ pages, fieldGroups: [...userStripped, ...merged] }, parsedFieldNames)
}

export function parseOptionsInput(raw: string): string[] {
  return raw.split(',').map(s => s.trim()).filter(Boolean)
}
